import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { ApiError, apiRequest, queryClient } from '@/lib/queryClient';
import { useAuth } from './AuthContext';

interface ImpersonationState {
  active: boolean;
  targetUserId: number | null;
  targetDisplayName: string | null;
  targetRole: string | null;
  targetFinancesUnclaimedAccess: boolean;
  sessionId: number | null;
}

interface ImpersonationContextType {
  impersonation: ImpersonationState;
  startImpersonation: (targetUserId: number, reason?: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  isLoading: boolean;
  /** True until /status has resolved for the current user. */
  statusLoading: boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType | null>(null);

const EMPTY_STATE: ImpersonationState = {
  active: false,
  targetUserId: null,
  targetDisplayName: null,
  targetRole: null,
  targetFinancesUnclaimedAccess: false,
  sessionId: null,
};

export function ImpersonationProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [impersonation, setImpersonation] = useState<ImpersonationState>(EMPTY_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusAttempt, setStatusAttempt] = useState(0);

  // Only admins can use View-as: /api/admin/impersonation/* is behind AdminGuard.
  const isAdmin = user?.role && ['SYSTEM_ADMIN', 'ADMIN'].includes(user.role);

  useEffect(() => {
    if (!isAdmin) {
      setImpersonation(EMPTY_STATE);
      setStatusLoading(false);
      return;
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    setStatusLoading(true);
    apiRequest<{
      active: boolean;
      targetUserId?: number;
      targetDisplayName?: string;
      targetRole?: string;
      targetFinancesUnclaimedAccess?: boolean;
      sessionId?: number;
    }>('GET', '/api/admin/impersonation/status')
      .then((data) => {
        if (cancelled) return;
        if (data.active) {
          setImpersonation({
            active: true,
            targetUserId: data.targetUserId!,
            targetDisplayName: data.targetDisplayName!,
            targetRole: data.targetRole!,
            targetFinancesUnclaimedAccess: data.targetFinancesUnclaimedAccess === true,
            sessionId: data.sessionId!,
          });
        } else {
          setImpersonation(EMPTY_STATE);
        }
        setStatusLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        // 401/403: /status sits behind the same admin-only guard as /start, so
        // this user cannot hold a View-as session (e.g. demoted since page
        // load). Resolve as not impersonating instead of retrying forever (AR-457).
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          setImpersonation(EMPTY_STATE);
          setStatusLoading(false);
          return;
        }
        // Otherwise stay unresolved: a failed /status must not fall back to the
        // admin's own access while a server-side impersonation may be active. Retry.
        retryTimer = setTimeout(
          () => setStatusAttempt((n) => n + 1),
          Math.min(30_000, 2_000 * 2 ** statusAttempt),
        );
      });

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [isAdmin, user?.id, statusAttempt]);

  const startImpersonation = useCallback(
    async (targetUserId: number, reason?: string) => {
      setIsLoading(true);
      try {
        const data = await apiRequest<{
          success: boolean;
          impersonation?: {
            targetUserId: number;
            targetDisplayName: string;
            targetRole: string;
            targetFinancesUnclaimedAccess?: boolean;
            sessionId: number;
          };
        }>('POST', '/api/admin/impersonation/start', { targetUserId, reason });

        if (data.success && data.impersonation) {
          setImpersonation({
            active: true,
            targetUserId: data.impersonation.targetUserId,
            targetDisplayName: data.impersonation.targetDisplayName,
            targetRole: data.impersonation.targetRole,
            targetFinancesUnclaimedAccess: data.impersonation.targetFinancesUnclaimedAccess === true,
            sessionId: data.impersonation.sessionId,
          });
          await queryClient.invalidateQueries();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  const endImpersonation = useCallback(async () => {
    setIsLoading(true);
    try {
      await apiRequest('POST', '/api/admin/impersonation/end');
      setImpersonation(EMPTY_STATE);
      await queryClient.invalidateQueries();
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <ImpersonationContext.Provider
      value={{ impersonation, startImpersonation, endImpersonation, isLoading, statusLoading }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation(): ImpersonationContextType {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return ctx;
}

/**
 * Unclaimed Commissions access for the member whose data is on screen: the
 * View-as target while impersonating (the /api/unclaimed routes resolve as the
 * target), otherwise the signed-in user. Undefined until impersonation status
 * has resolved, so the admin's own access is never used for the target.
 */
export function useUnclaimedAccess(): boolean | undefined {
  const { user } = useAuth();
  const { impersonation, statusLoading } = useImpersonation();
  if (statusLoading) return undefined;
  return impersonation.active
    ? impersonation.targetFinancesUnclaimedAccess
    : !!user?.financesUnclaimedAccess;
}
